import { IComboBox, IComboBoxOption } from '@fluentui/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Granularity } from '../../../telemetry/gen/ai/h2o/cloud/telemetry_read_api/v1/aiunits_pb';
import { TagFilterOption, TagFiltersResult } from '../types';

/**
 * Custom hook to manage tag filter state (date-range-scoped)
 *
 * Manages the selection state for tag filters. Filter options are dynamically
 * extracted from the prefetched chart data.
 *
 * The hook maintains two states:
 * - pendingTags: UI state that updates immediately on selection
 * - appliedTags: Committed state that triggers data fetches
 *
 * IMPORTANT: When date range changes, both pending and applied filters are automatically reset.
 * This is because tag filters are scoped to the current date range.
 *
 * @param tagOptions Array of tag filter options (extracted from chart data)
 * @param startTime Start of the current time range (for change detection)
 * @param endTime End of the current time range (for change detection)
 * @param granularity Granularity of the current time range (for change detection)
 * @returns Object containing tag options, pending/applied tags, handlers, and computed states
 */
export function useTagFilters(
  tagOptions: TagFilterOption[],
  startTime: Date,
  endTime: Date,
  granularity: Granularity
): TagFiltersResult {
  // Pending state: immediate UI updates, no fetch triggered
  const [pendingTags, setPendingTags] = useState<string[]>([]);

  // Applied state: committed filters that trigger data fetches
  const [appliedTags, setAppliedTags] = useState<string[]>([]);

  // Track previous date range to detect changes
  const prevDateRangeRef = useRef<string>('');
  const currentDateRangeKey = `${startTime.toISOString()}-${endTime.toISOString()}-${granularity}`;

  // Auto-reset filters when date range changes
  useEffect(() => {
    if (prevDateRangeRef.current !== '' && prevDateRangeRef.current !== currentDateRangeKey) {
      setPendingTags([]);
      setAppliedTags([]);
    }
    prevDateRangeRef.current = currentDateRangeKey;
  }, [currentDateRangeKey]);

  // Handle tag filter changes (updates pending state only, no fetch)
  const handleChange = useCallback(
    (_event: React.FormEvent<IComboBox>, option?: IComboBoxOption) => {
      if (!option) return;

      setPendingTags((prev) => {
        const updatedTags = option.selected
          ? [...prev, option.key as string]
          : prev.filter((tag) => tag !== option.key);

        return updatedTags;
      });
    },
    [] // No dependencies - stable callback reference
  );

  // Apply handler: commits pending changes to applied state (triggers fetch)
  const handleApply = useCallback(() => {
    setAppliedTags(pendingTags);
  }, [pendingTags]);

  // Reset handler: implements two scenarios
  // 1. If no applied filters: clear pending only (no fetch)
  // 2. If applied filters exist: clear both pending and applied (triggers fetch)
  const handleReset = useCallback(() => {
    setPendingTags([]);

    // Only clear applied filters if there were any (triggers fetch only if needed)
    if (appliedTags.length > 0) {
      setAppliedTags([]);
    }
  }, [appliedTags]);

  // Computed values for button states
  const hasChanges = JSON.stringify([...pendingTags].sort()) !== JSON.stringify([...appliedTags].sort());
  const hasFiltersToReset = pendingTags.length > 0 || appliedTags.length > 0;

  return {
    tagOptions,
    selectedTags: pendingTags,
    appliedTags,
    handleChange,
    handleApply,
    handleReset,
    hasChanges,
    hasFiltersToReset,
  };
}
